import React, { useState } from "react";
import axios from "axios";
import { Check, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-white border border-[#d9c3a8] px-4 py-3 rounded-md outline-none focus:ring-2 focus:ring-[#ebc754] text-[#1e231f] text-sm";

export const WaitlistForm = ({ slug, eventName }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", website: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setError("Please add your name and email.");
      return;
    }
    setStatus("submitting");
    setError("");
    try {
      await axios.post(`${API}/waitlist`, { event: slug, ...form });
      setStatus("done");
    } catch (err) {
      setStatus("idle");
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  if (status === "done") {
    return (
      <div
        data-testid="waitlist-success"
        className="bg-white border border-[#d9c3a8] rounded-lg p-8 text-center"
      >
        <div className="w-14 h-14 rounded-full bg-[#e6f0e2] text-[#5b8049] flex items-center justify-center mx-auto mb-4">
          <Check size={26} />
        </div>
        <h3 className="feral-serif text-[#9b3d0a] text-2xl mb-2">You're on the waitlist</h3>
        <p className="text-[#6e5646] text-sm leading-relaxed">
          Thanks for requesting a place at {eventName || "this event"}. Places are limited — a member of the
          FERAL team will be in touch personally if a spot opens up for you. Check your inbox for a confirmation.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="waitlist-form"
      className="bg-white border border-[#d9c3a8] rounded-lg p-6 md:p-8 space-y-4"
    >
      {/* honeypot */}
      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={form.website}
        onChange={(e) => set("website", e.target.value)}
        className="hidden"
        aria-hidden="true"
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="block text-xs font-semibold text-[#7a6a58] mb-1">Full name</span>
          <input
            className={inputCls}
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            data-testid="waitlist-name"
            placeholder="Your name"
          />
        </label>
        <label className="block">
          <span className="block text-xs font-semibold text-[#7a6a58] mb-1">Email</span>
          <input
            type="email"
            className={inputCls}
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            data-testid="waitlist-email"
            placeholder="you@email.com"
          />
        </label>
      </div>
      <label className="block">
        <span className="block text-xs font-semibold text-[#7a6a58] mb-1">Phone</span>
        <input
          className={inputCls}
          value={form.phone}
          onChange={(e) => set("phone", e.target.value)}
          data-testid="waitlist-phone"
          placeholder="Your number"
        />
      </label>
      <label className="block">
        <span className="block text-xs font-semibold text-[#7a6a58] mb-1">
          Why do you want to attend? Tell us a little about you
        </span>
        <textarea
          className={`${inputCls} min-h-[110px] resize-y`}
          value={form.message}
          onChange={(e) => set("message", e.target.value)}
          data-testid="waitlist-message"
          placeholder="A few words about you and why you'd like a place"
        />
      </label>

      {error && (
        <p className="text-[#9b3d0a] text-sm bg-[#f3e7d6] border border-[#e0c9ad] rounded px-3 py-2" data-testid="waitlist-error">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        data-testid="waitlist-submit"
        className="w-full inline-flex items-center justify-center gap-2 bg-[#9b3d0a] hover:bg-[#7e3208] disabled:opacity-60 text-[#f2e5de] px-8 py-3.5 tracking-[0.12em] text-sm font-bold uppercase transition-colors"
      >
        {status === "submitting" ? "Sending…" : "Request a place"} <Send size={15} />
      </button>
      <p className="text-[#9a876f] text-xs text-center">
        Joining the waitlist doesn't guarantee a place.
      </p>
    </form>
  );
};

export default WaitlistForm;
